import { FunctionComponent, memo, useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  NewsletterConsentScreen,
  useMeOnboardingPendingStatusesQuery,
  UserOnboardingType,
  useUserUpdateOnboardingStatusMutation,
} from '../../services';
import {
  Button,
  ButtonType,
  FeedFollowBehavior,
  Modal,
  NewsletterConsent,
  useFollowableStories,
  useFollowableTags,
  useIsNewslettersAllowed,
  useIsPersonalizationEnabled,
  useNewsFeed,
} from '../../shared';
import { PersonalizedNewsFeed } from './PersonalizedNewsFeed';

import classes from './NewsFeedOnboardingModal.module.scss';

export const NewsFeedOnboardingModal: FunctionComponent = memo(() => {
  const { t } = useTranslation();

  const { getNewsFeedItems } = useNewsFeed();

  const { data: pendingStatuses } = useMeOnboardingPendingStatusesQuery();

  const [userUpdateOnboardingStatus] = useUserUpdateOnboardingStatusMutation();

  const { followableStories, isFetchingFollowableStories } = useFollowableStories({
    feedFollowBehavior: [FeedFollowBehavior.OPTIONAL_UNFOLLOW],
  });

  const { followableTags, isFetchingFollowableTags } = useFollowableTags({
    feedFollowBehavior: [FeedFollowBehavior.OPTIONAL_UNFOLLOW],
  });

  const isPersonalizationEnabled = useIsPersonalizationEnabled();

  const isNewslettersAllowed = useIsNewslettersAllowed();

  const titleId = useId();

  const [isOpen, setIsOpen] = useState(false);

  const [shownTypes, setShownTypes] = useState<UserOnboardingType[]>([]);

  const activeType: UserOnboardingType | undefined = shownTypes[shownTypes.length - 1];

  const hasContent: Record<UserOnboardingType, boolean> = {
    [UserOnboardingType.PERSONALIZED_NEWSFEED]:
      isPersonalizationEnabled && (followableStories.length > 0 || followableTags.length > 0),
    [UserOnboardingType.NEWSLETTER_CONSENT]: isNewslettersAllowed,
  };

  const nextType = Object.values(UserOnboardingType).find(
    (type) => pendingStatuses?.includes(type) && !shownTypes.includes(type) && hasContent[type]
  );

  const showStep = useCallback(
    (type: UserOnboardingType) => {
      setShownTypes([...shownTypes, type]);
      userUpdateOnboardingStatus({ type });
    },
    [shownTypes, userUpdateOnboardingStatus]
  );

  useEffect(() => {
    if (
      isFetchingFollowableStories ||
      isFetchingFollowableTags ||
      shownTypes.length > 0 ||
      !nextType
    ) {
      return;
    }

    setIsOpen(true);
    showStep(nextType);
  }, [
    isFetchingFollowableStories,
    isFetchingFollowableTags,
    nextType,
    shownTypes.length,
    showStep,
  ]);

  const leaveStep = useCallback(() => {
    if (activeType === UserOnboardingType.PERSONALIZED_NEWSFEED) {
      getNewsFeedItems();
    }
  }, [activeType, getNewsFeedItems]);

  const closeHandler = useCallback(() => {
    setIsOpen(false);
    leaveStep();
  }, [leaveStep]);

  const nextHandler = useCallback(() => {
    if (!nextType) {
      closeHandler();
      return;
    }

    leaveStep();
    showStep(nextType);
  }, [closeHandler, leaveStep, nextType, showStep]);

  const content = useMemo(() => {
    switch (activeType) {
      case UserOnboardingType.PERSONALIZED_NEWSFEED:
        return (
          <PersonalizedNewsFeed
            followableStories={followableStories}
            followableTags={followableTags}
            titleId={titleId}
          />
        );
      case UserOnboardingType.NEWSLETTER_CONSENT:
        return <NewsletterConsent screen={NewsletterConsentScreen.ONBOARDING} titleId={titleId} />;
      default:
        return null;
    }
  }, [activeType, followableStories, followableTags, titleId]);

  const body = useMemo(() => {
    if (!content) {
      return null;
    }

    return (
      <div className={classes['onboarding']}>
        {content}

        <div className={classes['onboarding__footer']}>
          <button type={'button'} className={classes['onboarding__later']} onClick={nextHandler}>
            {t('common.later')}
          </button>

          <Button
            type={ButtonType.primary}
            label={t(`common.${nextType ? 'next' : 'done'}`)}
            onClick={nextHandler}
          />
        </div>
      </div>
    );
  }, [content, nextHandler, nextType, t]);

  return (
    <Modal isOpen={isOpen} onClose={closeHandler} body={body} ariaLabelledBy={titleId} alignTop />
  );
});
